import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import site from "../content/site.json";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Stotrams", to: "/stotrams" },
  { label: "Authenticity", to: "/authenticity" },
  { label: "Contact", to: "/contact" },
];

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/85 border-b border-charcoal/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Left rail */}
          <div className="hidden md:flex flex-1 items-center gap-10">
            {NAV.slice(0, 2).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-xs tracking-[0.24em] uppercase font-medium text-charcoal hover:text-muted_gold transition-colors duration-500 ${
                    isActive ? "active text-muted_gold" : ""
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          {/* Center: small circular V icon placeholder */}
          <Link
            to="/"
            data-testid="nav-logo-icon"
            className="flex-shrink-0 flex items-center justify-center select-none"
            aria-label="TATTVAM home"
          >
            <img
              src={site.brand.logoIcon}
              alt={site.brand.logoIconAlt}
              className="h-10 w-10 md:h-11 md:w-11"
              data-testid="nav-logo-icon-img"
            />
          </Link>

          {/* Right rail */}
          <div className="hidden md:flex flex-1 items-center justify-end gap-10">
            {NAV.slice(2).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-xs tracking-[0.24em] uppercase font-medium text-charcoal hover:text-muted_gold transition-colors duration-500 ${
                    isActive ? "active text-muted_gold" : ""
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>

          {/* Mobile button */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            onClick={() => setOpen(!open)}
            className="md:hidden text-charcoal p-2"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-ivory border-t border-charcoal/5"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.24em] uppercase font-medium ${
                    isActive ? "text-muted_gold" : "text-charcoal"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
